import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import { Send as SendIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { supabase } from '../services/supabase';

// 자주 쓰는 문구 템플릿 — 매번 새로 쓰지 않도록
const TEMPLATES = [
  {
    key: 'notice',
    label: '공지사항',
    type: 'notice',
    title: '📢 새로운 공지가 등록되었습니다',
    body: '앱에서 확인해주세요.',
  },
  {
    key: 'event',
    label: '이벤트 안내',
    type: 'event',
    title: '🎁 이벤트가 진행 중입니다',
    body: '응모권을 모아 지금 응모해보세요!',
  },
  {
    key: 'event_close',
    label: '이벤트 마감 임박',
    type: 'event',
    title: '⏰ 이벤트 마감이 얼마 남지 않았어요',
    body: '오늘 21시에 응모가 마감됩니다. 잊지 말고 응모하세요!',
  },
];

function PushNotifications() {
  const [type, setType] = useState('notice');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [mode, setMode] = useState('topic');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [audience, setAudience] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  // 발송 대상 규모를 미리 보여준다 (dryRun — 실제 발송 없음)
  const loadAudience = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'send-broadcast-notification',
        { body: { title: 'preview', body: 'preview', type: 'notice', dryRun: true } }
      );
      if (fnError) throw fnError;
      setAudience(data);
    } catch (err) {
      console.error('발송 대상 조회 실패:', err);
      setError(
        'send-broadcast-notification 엣지 함수를 호출할 수 없습니다. 배포 상태를 확인해주세요.'
      );
    }
  };

  useEffect(() => {
    loadAudience();
  }, []);

  const applyTemplate = (key) => {
    const t = TEMPLATES.find((x) => x.key === key);
    if (!t) return;
    setType(t.type);
    setTitle(t.title);
    setBody(t.body);
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    setSuccess('');
    setLastResult(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'send-broadcast-notification',
        { body: { title: title.trim(), body: body.trim(), type, mode } }
      );
      if (fnError) throw fnError;

      if (data?.success === false) {
        setError(`발송 실패: ${data.error || '알 수 없는 오류'}`);
        setLastResult(data);
        return;
      }

      setLastResult(data);
      if (data?.mode === 'topic') {
        setSuccess('전체 사용자에게 푸시를 발송했습니다. (토픽 발송)');
      } else {
        setSuccess(
          `푸시 발송 완료 — 성공 ${data?.sent ?? 0}건 / 실패 ${data?.failed ?? 0}건 (대상 ${data?.total ?? 0}명)`
        );
      }
    } catch (err) {
      console.error('푸시 발송 실패:', err);
      setError(`발송에 실패했습니다: ${err.message || err}`);
    } finally {
      setSending(false);
    }
  };

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        푸시 발송
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        전체 사용자에게 공지/이벤트 푸시를 보냅니다. 당첨·출금 푸시는 해당 처리 시
        자동으로 발송되므로 여기서 보낼 필요가 없습니다.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
              템플릿
            </Typography>
            {TEMPLATES.map((t) => (
              <Chip
                key={t.key}
                label={t.label}
                onClick={() => applyTemplate(t.key)}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>알림 종류</InputLabel>
              <Select value={type} label="알림 종류" onChange={(e) => setType(e.target.value)}>
                <MenuItem value="notice">공지 (탭하면 공지사항으로 이동)</MenuItem>
                <MenuItem value="event">이벤트 (탭하면 응모권 확인으로 이동)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>발송 방식</InputLabel>
              <Select value={mode} label="발송 방식" onChange={(e) => setMode(e.target.value)}>
                <MenuItem value="topic">전체 발송 (토픽 · 권장)</MenuItem>
                <MenuItem value="tokens">개별 발송 (성공/실패 건수 확인)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 60 }}
            helperText={`${title.length}/60 — 알림바에 굵게 표시됩니다`}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="내용"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            rows={3}
            inputProps={{ maxLength: 160 }}
            helperText={`${body.length}/160 — 길면 알림바에서 잘릴 수 있습니다`}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!canSend}
              onClick={() => setConfirmOpen(true)}
            >
              {sending ? '발송 중...' : '전체 발송'}
            </Button>
            {audience && (
              <Typography variant="body2" color="text.secondary">
                {mode === 'topic'
                  ? `토픽 "${audience.topic}" 구독자 전체 (앱 설치 사용자)`
                  : `알림 토큰 보유 ${audience.token_holders}명`}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 미리보기 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            미리보기
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              p: 2,
              bgcolor: '#fafafa',
              maxWidth: 420,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
              <CampaignIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                황금거북이
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {title || '제목이 여기에 표시됩니다'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {body || '내용이 여기에 표시됩니다'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {lastResult && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              마지막 발송 결과
            </Typography>
            <Box
              component="pre"
              sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {JSON.stringify(lastResult, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>전체 푸시 발송</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <b>모든 사용자에게 즉시 발송되며 취소할 수 없습니다.</b> 내용을 다시 확인해주세요.
          </Alert>
          <DialogContentText component="div">
            <b>{title}</b>
            <br />
            {body}
            <br />
            <br />
            종류: {type === 'notice' ? '공지' : '이벤트'} · 방식:{' '}
            {mode === 'topic' ? '토픽 전체 발송' : '토큰 개별 발송'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>취소</Button>
          <Button variant="contained" color="warning" onClick={handleSend}>
            발송
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PushNotifications;
